import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  IconButton,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { ReactComponent as PdfIcon } from "../../../assets/svg/SmallPdfIcon.svg";
import { MdOutlineCalendarMonth } from "react-icons/md";
import { RiBillLine } from "react-icons/ri";
import moment from "moment";
import "../../RFQPricing/components/Review/review.css";
import { ReactComponent as ArrowIcon } from "../../../assets/svg/ArrowIcon.svg";
import { renderCurrencyIconColored } from "../../../utils/CurrencyUtils";
import { formatIntegerAmount } from "../../../utils/CommonFunction";
import { getProposalViewPayments } from "../../../services/contractorService";

const RENO_FEE_TYPE_LABEL = {
  CUSTOMER_ONLY: "Customer Paid",
  CONTRACTOR_ONLY: "Contractor Paid",
  SPLIT: "Split",
};

const TIMELINE_COLORS = {
  Approvals: "#4D6BF6",
  "Demolition & Site Prep": "#FA832E",
  Construction: "#22C55E",
  "Finishing & Handover": "#A855F7",
};

const TIMELINE_PHASES = [
  "Approvals",
  "Demolition & Site Prep",
  "Construction",
  "Finishing & Handover",
];

const computeWeekRanges = (timeline) => {
  const ranges = [];
  let cursor = 1;
  TIMELINE_PHASES.forEach((phase) => {
    const weeks = parseInt(timeline?.[phase]) || 0;
    if (weeks > 0) {
      const startWeek = cursor;
      const endWeek = cursor + weeks - 1;
      ranges.push({
        phase,
        startWeek,
        endWeek,
        weeks,
        color: TIMELINE_COLORS[phase],
      });
      cursor = endWeek + 1;
    }
  });
  return ranges;
};

const TimelineTrack = ({ ranges }) => (
  <Box
    sx={{
      display: "flex",
      backgroundColor: "#fff",
      border: "1px solid #E5E7EB",
      borderRadius: "12px",
      overflow: "hidden",
      flexWrap: { xs: "wrap", sm: "nowrap" },
    }}
  >
    {ranges.map((r, i) => (
      <Box
        key={i}
        sx={{
          flex: { xs: `1 1 50%`, sm: r.weeks },
          padding: "14px",
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          borderRight: { xs: "none", sm: "1px solid #F3F4F6" },
          borderBottom: { xs: "1px solid #F3F4F6", sm: "none" },
          position: "relative",
          "&:last-of-type": {
            borderRight: "none",
            borderBottom: "none",
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "3px",
            backgroundColor: r.color,
          },
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: r.color }}>
          Week{" "}
          {r.startWeek === r.endWeek
            ? r.startWeek
            : `${r.startWeek}-${r.endWeek}`}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#0D0D0E", fontWeight: 500 }}>
          {r.phase}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#6B7280" }}>
          {r.weeks} {r.weeks === 1 ? "week" : "weeks"}
        </Typography>
      </Box>
    ))}
  </Box>
);

const SectionCard = ({ title, children }) => (
  <Card
    sx={{
      borderRadius: "12px",
      boxShadow: "none",
      margin: "1vh 0",
      border: "1px solid #E5E7EB",
    }}
  >
    <CardContent sx={{ padding: "20px" }}>
      <Box
        sx={{
          fontSize: 16,
          fontWeight: 600,
          color: "#0D0D0E",
          mb: 2,
          fontFamily: "Inter Tight",
        }}
      >
        {title}
      </Box>
      {children}
    </CardContent>
  </Card>
);

const Field = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography sx={{ fontSize: 12, color: "#9CA3AF", fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 14,
        color: value ? "#0D0D0E" : "#9CA3AF",
        whiteSpace: "pre-wrap",
      }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

const DeliverableSummaryRow = ({ deliverable, index }) => {
  const subs = deliverable.sub_activities || [];
  const [expanded, setExpanded] = useState(false);
  const hasSubs = subs.length > 0;

  return (
    <Box
      sx={{
        borderBottom: "1px solid #F3F4F6",
        width: "100%",
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box
        onClick={hasSubs ? () => setExpanded((v) => !v) : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          py: 0.75,
          cursor: hasSubs ? "pointer" : "default",
          "&:hover": hasSubs ? { backgroundColor: "#F9FAFB" } : {},
          borderRadius: "6px",
          px: 0.5,
        }}
      >
        <Typography
          sx={{
            flex: 1,
            fontSize: 13,
            color: "#0D0D0E",
            fontWeight: 500,
          }}
        >
          {index + 1}.{" "}
          {deliverable.deliverable_description || deliverable.name || "—"}
        </Typography>
        {hasSubs && (
          <>
            <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>
              {subs.length} sub-{subs.length === 1 ? "activity" : "activities"}
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
              sx={{ flexShrink: 0 }}
            >
              {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </>
        )}
      </Box>
      <Collapse in={expanded && hasSubs} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 3, pb: 1 }}>
          {subs.map((s, si) => (
            <Typography
              key={si}
              sx={{
                fontSize: 12,
                color: "#6B7280",
                py: 0.4,
                borderBottom:
                  si === subs.length - 1 ? "none" : "1px dashed #F3F4F6",
              }}
            >
              {index + 1}.{si + 1}{" "}
              {s.sub_activity_description || s.name || "—"}
            </Typography>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const MilestoneSummaryCard = ({ milestone, index }) => {
  const deliverables = Array.isArray(milestone.deliverables)
    ? milestone.deliverables
    : [];
  const [expanded, setExpanded] = useState(false);
  const hasDeliverables = deliverables.length > 0;

  return (
    <Box
      sx={{
        border: "1px solid #e5e7eb",
        borderRadius: "10px",
        background: "#fff",
        mb: 1,
        overflow: "hidden",
      }}
    >
      
      <Box
        onClick={hasDeliverables ? () => setExpanded((v) => !v) : undefined}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          padding: "10px 14px",
          cursor: hasDeliverables ? "pointer" : "default",
          "&:hover": hasDeliverables ? { backgroundColor: "#F9FAFB" } : {},
        }}
      >
        <Box className="rfq-billing-badge">{index + 1}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box className="rfq-billing-name">
            {milestone.description || milestone.name || "New Milestone"}
          </Box>
          <Box className="rfq-billing-pct">
            {milestone.percentage || 0}% of total
            {hasDeliverables && (
              <Box component="span" sx={{ ml: 1, color: "#9CA3AF" }}>
                · {deliverables.length}{" "}
                {deliverables.length === 1 ? "deliverable" : "deliverables"}
              </Box>
            )}
          </Box>
        </Box>
        {Number(milestone.amount) > 0 && (
          <Box
            sx={{
              fontSize: 13,
              color: "#0D0D0E",
              fontFamily: "Inter Tight",
              fontWeight: 600,
              whiteSpace: "nowrap",
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {renderCurrencyIconColored("black-xs-small")}
            {formatAmount(milestone.amount)}
          </Box>
        )}
        {milestone.due_date && (
          <Box
            sx={{
              fontSize: 12,
              color: "#6B7280",
              fontFamily: "Inter Tight",
              whiteSpace: "nowrap",
              flexShrink: 0,
              mr: 0.5,
            }}
          >
            Due {moment(milestone.due_date).format("MMM DD, YYYY")}
          </Box>
        )}
        {hasDeliverables ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            sx={{ flexShrink: 0, color: "#9CA3AF" }}
          >
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        ) : (
          <Box sx={{ width: 32, height: 32, flexShrink: 0 }} />
        )}
      </Box>
      <Collapse in={expanded && hasDeliverables} timeout="auto" unmountOnExit>
        <Box
          sx={{
            padding: "0 14px 12px",
            borderTop: "1px solid #F3F4F6",
            backgroundColor: "#FAFAFB",
          }}
        >
          <Box sx={{ px:2, pt: 1, display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
            {deliverables.map((d, di) => (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, marginLeft: "1vw" }}>
              <DeliverableSummaryRow
                key={d.id || di}
                deliverable={d}
                index={di}
              />
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

const formatAmount = (value) => formatIntegerAmount(value);

const PAYMENT_HEADER_SX = {
  fontSize: 13,
  fontWeight: 500,
  color: "#949DAA",
  fontFamily: "Inter Tight",
};

// Generic 3-column read-only table — Name | Due Date | Amount — used for
// both Customer Payments and Contractor Settlements in the Review tab.
const PaymentsLikeTable = ({
  rows,
  emptyLabel = "No payments configured.",
  showMilestone = false,
}) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const total = safeRows.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);

  if (safeRows.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
        {emptyLabel}
      </Typography>
    );
  }

  // The customer-facing table opt-ins to a 4th column showing which
  // billing milestone each payment line is mapped to. Other callers
  // (e.g. settlements) keep the original 3-column layout.
  const gridCols = showMilestone ? "1.3fr 1.2fr 1fr 1fr" : "1.4fr 1fr 1fr";

  return (
    <Box sx={{ width: { xs: "100%", md: showMilestone ? "100%" : "60%" } }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: gridCols,
          columnGap: 3,
          alignItems: "center",
          mb: 1,
          px: 0.5,
        }}
      >
        <Typography sx={PAYMENT_HEADER_SX}>Payment Name</Typography>
        {showMilestone && (
          <Typography sx={PAYMENT_HEADER_SX}>Billing Milestone</Typography>
        )}
        <Typography sx={PAYMENT_HEADER_SX}>Due Date</Typography>
        <Typography sx={{ ...PAYMENT_HEADER_SX, textAlign: "right" }}>
          Amount ({renderCurrencyIconColored("black-xs-small")})
        </Typography>
      </Box>
      {safeRows.map((r, idx) => {
        const dueLabel = r.dueDate
          ? moment(r.dueDate).format("MMM DD, YYYY")
          : "-";
        return (
          <Box
            key={r.id ?? idx}
            sx={{
              display: "grid",
              gridTemplateColumns: gridCols,
              columnGap: 3,
              alignItems: "center",
              py: 1.5,
              px: 0.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                color: "#0D0D0E",
                fontFamily: "Inter Tight",
                fontWeight: 500,
              }}
            >
              {r.name || "—"}
            </Typography>
            {showMilestone && (
              <Typography
                sx={{
                  fontSize: 13,
                  color: r.milestoneName ? "#0D0D0E" : "#9CA3AF",
                  fontFamily: "Inter Tight",
                }}
              >
                {r.milestoneName || "—"}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: 13,
                color: r.dueDate ? "#0D0D0E" : "#6B7280",
                fontFamily: "Inter Tight",
              }}
            >
              {dueLabel}
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                color: "#0D0D0E",
                fontFamily: "Inter Tight",
                fontWeight: 500,
                textAlign: "right",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 0.5,
              }}
            >
              {formatAmount(r.amount)}
            </Typography>
          </Box>
        );
      })}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1.5,
          mt: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
          }}
        >
          Total Amount:
        </Typography>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {renderCurrencyIconColored("small")}
          {formatAmount(total)}
        </Typography>
      </Box>
    </Box>
  );
};

// Reno Fee details — Tier, selected reno-fee type, amount. The /reno-fees
// payload always includes a `renoFee` tier-defaults object; the setup
// branch additionally carries a `billingId` (plus `strategy`, `renoFees`,
// etc). billingId presence is the only reliable setup signal — `renoFee`
// alone is not, since the backend keeps it populated in both branches.
const RenoFeeDetails = ({ data }) => {
  const notSetup =
    data?.billingId === undefined ||
    data?.billingId === null ||
    data?.billingId === "";
  // Tier always lives on the inner `renoFee` object — both pre- and
  // post-setup. Older payloads also surfaced applicablePlanName at the
  // root, so we fall back there as a last resort.
  const tierLabel =
    data?.renoFee?.applicablePlanName ||
    data?.renoFee?.applicablePlanCode ||
    data?.applicablePlanName ||
    data?.applicablePlanCode ||
    "—";
  const typeLabel = notSetup
    ? "Not Configured"
    : RENO_FEE_TYPE_LABEL[data?.strategy] || "—";
  const amount = notSetup
    ? Number(data?.renoFee?.renoFee) || 0
    : Number(data?.renoFees) || 0;

  const rows = [
    {
      label: "Tier",
      value: (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.25,
            py: 0.25,
            borderRadius: "999px",
            backgroundColor: "#F3E8FF",
            color: "#7C3AED",
            fontFamily: "Inter Tight",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {tierLabel}
        </Box>
      ),
    },
    {
      label: "Reno Fee Type",
      value: (
        <Typography
          sx={{
            fontSize: 14,
            color: "#0D0D0E",
            fontFamily: "Inter Tight",
            fontWeight: 600,
          }}
        >
          {typeLabel}
        </Typography>
      ),
    },
    {
      label: "Amount",
      value: (
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          {renderCurrencyIconColored("black-xs-small")}
          {formatAmount(amount)}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: { xs: "100%", md: "60%" } }}>
      {rows.map((r) => (
        <Box
          key={r.label}
          sx={{
            display: "grid",
            gridTemplateColumns: "200px 1fr",
            alignItems: "center",
            px: 2,
            py: 1.25,
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              color: "#6B7280",
              fontFamily: "Inter Tight",
              fontWeight: 500,
            }}
          >
            {r.label}
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              fontSize: 14,
              color: "#0D0D0E",
              fontFamily: "Inter Tight",
              fontWeight: 600,
            }}
          >
            {r.value}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const Review = ({
  proposalId,
  projectName,
  projectImage,
  projectLocation,
  proposalDescription,
  proposalValue,
  customerDetails,
  selectedCustomer,
  billingMilestones,
  timeline,
  contract,
  renoContract,
}) => {
  const weekRanges = computeWeekRanges(timeline);

  // Fetched from GET /api/v2/contractor/proposal/{id}/reno-fees —
  // shape varies depending on whether reno-fees are setup yet.
  const [viewPayments, setViewPayments] = useState(null);
  const [viewPaymentsLoading, setViewPaymentsLoading] = useState(false);

  useEffect(() => {
    if (!proposalId) return undefined;
    let cancelled = false;
    (async () => {
      setViewPaymentsLoading(true);
      try {
        const res = await getProposalViewPayments(proposalId);
        if (cancelled) return;
        setViewPayments(res?.data || res || null);
      } catch (e) {
        console.error("Failed to fetch view-payments for review:", e);
      } finally {
        if (!cancelled) setViewPaymentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [proposalId]);

  // billingId presence is the source-of-truth for reno-fees being setup —
  // the legacy "no renoFee object" check no longer works because the
  // backend now ships the tier defaults under `renoFee` in both branches.
  const isSetup =
    !!viewPayments &&
    viewPayments.billingId !== undefined &&
    viewPayments.billingId !== null &&
    viewPayments.billingId !== "";

  // Look up: billingMilestoneId → groupName, sourced from the proposal's
  // billing_milestones list. Used to surface the milestone name on each
  // customer payment row.
  const milestoneNameById = React.useMemo(() => {
    const map = new Map();
    (billingMilestones || []).forEach((m) => {
      if (m && m.id != null) {
        map.set(
          String(m.id),
          m.groupName || m.description || `Milestone ${m.id}`,
        );
      }
    });
    return map;
  }, [billingMilestones]);

  const customerPaymentRows = isSetup
    ? (viewPayments?.paymentPlan?.paymentSchedule || []).map((s, idx) => ({
        id: `cust-${idx}`,
        name: s.displayName || "",
        dueDate: s.dueDate || "",
        amount: Number(s.amount) || 0,
        milestoneName:
          s.billingMilestoneId != null
            ? milestoneNameById.get(String(s.billingMilestoneId)) || ""
            : "",
      }))
    : [];

  // Settlement rows mirror the contractor payout list, but the Initial
  // Deposit row always anchors at the top regardless of where the
  // backend slots it in the array.
  const settlementRows = isSetup
    ? (() => {
        const list = viewPayments?.contractorPayouts || [];
        const isInitial = (s) =>
          s?.referenceType === "PROJECT_INITIAL_DEPOSIT" ||
          s?.paymentGroup?.type === "PROJECT_INITIAL_DEPOSIT";
        const ordered = [
          ...list.filter(isInitial),
          ...list.filter((s) => !isInitial(s)),
        ];
        return ordered.map((s, idx) => ({
          id: `set-${idx}`,
          name: s.paymentGroup?.groupName || s.paymentGroupName || "",
          dueDate: s.dueDate || s.paymentGroup?.dueDate || "",
          amount: Number(s.amount) || 0,
        }));
      })()
    : [];
  const customerName =
    selectedCustomer?.username || customerDetails?.username || "";
  const customerEmail =
    selectedCustomer?.customer_email || customerDetails?.customer_email || "";

  const [imageSrc, setImageSrc] = React.useState("");
  React.useEffect(() => {
    if (!projectImage) {
      setImageSrc("");
      return;
    }
    if (typeof projectImage === "string") {
      setImageSrc(projectImage);
      return;
    }
    const url = URL.createObjectURL(projectImage);
    setImageSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [projectImage]);

  const [contractUrl, setContractUrl] = React.useState("");
  React.useEffect(() => {
    if (!contract) {
      setContractUrl("");
      return;
    }
    if (typeof contract === "string") {
      setContractUrl(contract);
      return;
    }
    const url = URL.createObjectURL(contract);
    setContractUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [contract]);

  const [renoContractUrl, setRenoContractUrl] = React.useState("");
  React.useEffect(() => {
    if (!renoContract) {
      setRenoContractUrl("");
      return;
    }
    if (typeof renoContract === "string") {
      setRenoContractUrl(renoContract);
      return;
    }
    const url = URL.createObjectURL(renoContract);
    setRenoContractUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [renoContract]);

  const handleContractPreview = () => {
    if (contractUrl) {
      window.open(contractUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleRenoContractPreview = () => {
    if (renoContractUrl) {
      window.open(renoContractUrl, "_blank", "noopener,noreferrer");
    }
  };

  const renderContractTile = (file, onPreview, fallbackLabel) => (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1.5,
        padding: "10px 14px",
        border: "1px solid #E5E7EB",
        borderRadius: "10px",
        backgroundColor: "#F9FAFB",
      }}
    >
      <PdfIcon style={{ width: 28, height: 28, flexShrink: 0 }} />
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: "#0D0D0E",
          maxWidth: 220,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {typeof file === "string"
          ? file.split("/").pop() || fallbackLabel
          : file.name || fallbackLabel}
      </Typography>
      <IconButton
        size="small"
        onClick={onPreview}
        title="Preview"
        sx={{ color: "#4D6BF6" }}
      >
        <VisibilityOutlinedIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <Box>
      <SectionCard title="Project Details">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1.5, md: 2.5 },
            alignItems: { xs: "stretch", sm: "flex-start" },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {imageSrc ? (
              <Box
                sx={{
                  width: { xs: "100%", sm: 200, md: 240 },
                  height: { xs: 220, sm: 200, md: 240 },
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                  overflow: "hidden",
                  backgroundColor: "#F9FAFB",
                }}
              >
                <img
                  src={imageSrc}
                  alt="Project"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: { xs: "100%", sm: 200, md: 240 },
                  height: { xs: 220, sm: 200, md: 240 },
                  borderRadius: "12px",
                  border: "1px dashed #D1D5DB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#9CA3AF",
                  fontSize: 12,
                  backgroundColor: "#F9FAFB",
                }}
              >
                No image
              </Box>
            )}
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              <Field label="Proposal Name" value={projectName} />
              <Box sx={{ mb: 1 }}>
                <Typography
                  sx={{ fontSize: 12, color: "#9CA3AF", fontWeight: 500 }}
                >
                  Proposal Value
                </Typography>
                {proposalValue ? (
                  <Box
                    sx={{
                      fontSize: 14,
                      color: "#0D0D0E",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {renderCurrencyIconColored("black-xs-small")}
                    {formatAmount(proposalValue)}
                  </Box>
                ) : (
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: "#9CA3AF",
                    }}
                  >
                    —
                  </Typography>
                )}
              </Box>
            </Box>
            <Field label="Project Location" value={projectLocation} />
            {/* Scope of Work — inline on phone (xs) and desktop (md+); on tablet (sm) it lives below */}
            <Box sx={{ display: { xs: "block", sm: "none", md: "block" } }}>
              <Field label="Scope of Work" value={proposalDescription} />
            </Box>
          </Box>
        </Box>
        {/* Tablet-only Scope of Work as full-width row below the image+fields block */}
        <Box sx={{ display: { xs: "none", sm: "block", md: "none" }, mt: 2 }}>
          <Field label="Scope of Work" value={proposalDescription} />
        </Box>
      </SectionCard>

      <SectionCard title="Customer Details">
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <Field label="Customer Name" value={customerName} />
          <Field label="Customer Email" value={customerEmail} />
        </Box>
      </SectionCard>

      <SectionCard
        title={
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <MdOutlineCalendarMonth color="#4D6BF6" size={20} />
            Project Timeline
          </Box>
        }
      >
        {weekRanges.length > 0 ? (
          <TimelineTrack ranges={weekRanges} />
        ) : (
          <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
            No timeline added.
          </Typography>
        )}
      </SectionCard>

      <SectionCard
        title={
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <RiBillLine color="#fa832e" size={20} />
            Billing Schedule Summary
          </Box>
        }
      >
        {!billingMilestones || billingMilestones.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
            No milestones yet.
          </Typography>
        ) : (
          <Box className="rfq-billing-summary">
            {billingMilestones.map((milestone, index) => (
              <MilestoneSummaryCard
                key={milestone.id || `summary-${index}`}
                milestone={milestone}
                index={index}
              />
            ))}
          </Box>
        )}
      </SectionCard>

      <SectionCard
        title={
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <RiBillLine color="#4D6BF6" size={20} />
            Reno Fee Details
          </Box>
        }
      >
        {viewPaymentsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
            <CircularProgress size={18} />
          </Box>
        ) : viewPayments ? (
          <RenoFeeDetails data={viewPayments} />
        ) : (
          <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
            No reno fee details available.
          </Typography>
        )}
      </SectionCard>

      <SectionCard
        title={
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <RiBillLine color="#4D6BF6" size={20} />
            Contractor Settlements
          </Box>
        }
      >
        {viewPaymentsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
            <CircularProgress size={18} />
          </Box>
        ) : (
          <PaymentsLikeTable
            rows={settlementRows}
            emptyLabel="No settlements configured."
          />
        )}
      </SectionCard>

      <SectionCard
        title={
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <RiBillLine color="#4D6BF6" size={20} />
            Customer Payments
          </Box>
        }
      >
        {viewPaymentsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
            <CircularProgress size={18} />
          </Box>
        ) : (
          <PaymentsLikeTable
            rows={customerPaymentRows}
            emptyLabel="No customer payments configured."
            showMilestone
          />
        )}
      </SectionCard>

      <SectionCard
        title={
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <PdfIcon style={{ width: 22, height: 22, flexShrink: 0 }} />
            Contracts
          </Box>
        }
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 12,
                color: "#9CA3AF",
                fontWeight: 500,
                mb: 0.75,
              }}
            >
              Contract
            </Typography>
            {contract ? (
              renderContractTile(
                contract,
                handleContractPreview,
                "Contract.pdf"
              )
            ) : (
              <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
                No contract uploaded.
              </Typography>
            )}
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 12,
                color: "#9CA3AF",
                fontWeight: 500,
                mb: 0.75,
              }}
            >
              Reno Contract
            </Typography>
            {renoContract ? (
              renderContractTile(
                renoContract,
                handleRenoContractPreview,
                "RenoContract.pdf"
              )
            ) : (
              <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
                No Reno contract uploaded.
              </Typography>
            )}
          </Box>
        </Box>
      </SectionCard>
    </Box>
  );
};

export default Review;
